import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import Header from "../Header";
import Footer from "../Footer";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { 
  UserIcon,
  StarIcon,
  MapPinIcon,
  AwardIcon,
  GraduationCapIcon,
  CalendarIcon,
  ClockIcon,
  PhoneIcon,
  CheckCircleIcon,
  BookOpenIcon,
  HeartIcon
} from "lucide-react";
import { doctorService } from "../../services";
import { openWhatsApp } from "../../lib/utils";
import BookAppointmentModal from "../BookAppointmentModal";

const DoctorProfile = () => {
  const { doctorId } = useParams();
  const [selectedTimeSlot, setSelectedTimeSlot] = useState("");
  const [doctor, setDoctor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isBookingOpen, setIsBookingOpen] = useState(false);

  useEffect(() => {
    const fetchDoctor = async () => {
      try {
        setLoading(true);
        const doctorData = await doctorService.getDoctorById(doctorId);
        setDoctor(doctorData);
      } catch (err) {
        console.error('Error fetching doctor:', err);
        setError('Failed to load doctor information');
        // Fallback to mock data
        setDoctor({
          id: doctorId,
          name: "Dr. Rajesh Kumar",
          specialty: "General Surgery",
          qualification: "MBBS, MS (General Surgery)",
          experience: "15+ years",
          rating: 4.8,
          reviews: 234,
          consultationFee: 800,
          location: "Delhi",
          hospital: "TheLivCure Elantis",
          languages: ["English", "Hindi"],
          about: "Dr. Rajesh Kumar is a highly experienced General Surgeon with over 15 years of expertise in minimally invasive surgical procedures. He specializes in laparoscopic surgeries and has successfully performed over 3000 operations with excellent patient outcomes.",
          education: [
            "MBBS from All India Institute of Medical Sciences (AIIMS), Delhi",
            "MS in General Surgery from Post Graduate Institute (PGI), Chandigarh",
            "Advanced Training in Laparoscopic Surgery from Germany"
          ],
          specializations: [
            "Laparoscopic Surgery",
            "Gallbladder Surgery", 
            "Hernia Repair",
            "Appendix Surgery",
            "General Surgery"
          ],
          achievements: [
            "Performed 3000+ successful surgeries",
            "Gold Medalist in MS Surgery",
            "Member of Association of Minimal Access Surgeons",
            "Published 25+ research papers",
            "Best Doctor Award 2023"
          ],
          availableSlots: [
            "10:00 AM", "11:00 AM", "2:00 PM", "3:00 PM", "4:00 PM", "5:00 PM"
          ]
        });
      } finally {
        setLoading(false);
      }
    };

    fetchDoctor();
  }, [doctorId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600">Loading doctor information...</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !doctor) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <div className="text-red-600 mb-4">
            <UserIcon className="w-16 h-16 mx-auto mb-2" />
            <h2 className="text-2xl font-bold">Doctor Not Found</h2>
            <p className="text-gray-600 mt-2">{error || "The requested doctor profile could not be found."}</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Derived safe fields for rendering
  const displayName = doctor.fullName || doctor.name || "Doctor";
  const displayRating = doctor.rating || 0;
  const displayReviews = doctor.reviewCount || doctor.reviews || 0;
  const displayExperience = doctor.experience || "N/A";
  const displayConsultationFee = doctor.consultationFees ?? doctor.consultationFee ?? 0;
  const locationText = doctor.cities?.[0] || doctor.city || "N/A";
  const languagesList = Array.isArray(doctor.languages) ? doctor.languages : [];
  const educationList = Array.isArray(doctor.education)
    ? doctor.education
    : (Array.isArray(doctor.qualifications)
        ? doctor.qualifications.map(q => q.degree || "").filter(q => q)
        : []);
  const specializationsList = Array.isArray(doctor.specializations)
    ? doctor.specializations
    : [];
  const achievementsList = Array.isArray(doctor.achievements) ? doctor.achievements : [];
  const availableSlots = Array.isArray(doctor.availableSlots)
    ? doctor.availableSlots
    : ["10:00 AM", "11:00 AM", "2:00 PM", "3:00 PM"];
  const contactNumber = doctor.contactNumber || "9311720045";

  const patientReviews = [
    {
      name: "Priya Sharma",
      rating: 5,
      date: "January 2025",
      review: "Dr. Rajesh explained everything clearly and the surgery went perfectly. Recovery was faster than expected."
    },
    {
      name: "Amit Singh", 
      rating: 5,
      date: "December 2024",
      review: "Excellent doctor with great expertise. Very caring and professional throughout the treatment."
    },
    {
      name: "Sunita Patel",
      rating: 4,
      date: "December 2024", 
      review: "Good experience overall. The doctor was knowledgeable and the procedure was successful."
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Doctor Profile Header */}
      <section className="bg-gradient-to-br from-blue-50 to-cyan-50 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="overflow-hidden">
            <CardContent className="p-0">
              <div className="grid grid-cols-1 lg:grid-cols-3">
                {/* Doctor Image & Basic Info */}
                <div className="bg-gradient-to-br from-blue-100 to-purple-100 p-4 sm:p-6 lg:p-8 flex flex-col items-center justify-center text-center">
                  {doctor.profileImageUrl ? (
                    <img
                      src={doctor.profileImageUrl}
                      alt={displayName}
                      className="w-24 h-24 sm:w-28 sm:h-28 lg:w-32 lg:h-32 rounded-full object-cover border-4 border-white shadow-lg mb-4 sm:mb-6"
                    />
                  ) : (
                    <div className="w-24 h-24 sm:w-28 sm:h-28 lg:w-32 lg:h-32 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mb-4 sm:mb-6">
                      <UserIcon className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 text-white" />
                    </div>
                  )}
                  <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
                    {doctor.professionalTitle || 'Dr.'} {displayName}
                  </h1>
                  <p className="text-blue-600 font-medium mb-3 text-sm sm:text-base">{doctor.specialty || doctor.mainCategory || ""}</p>
                  {doctor.employeeCode && (
                    <p className="text-xs text-gray-500 mb-2">Employee Code: {doctor.employeeCode}</p>
                  )}
                  <div className="flex items-center space-x-1 mb-3">
                    <StarIcon className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    <span className="font-semibold">{displayRating}</span>
                    <span className="text-gray-600">({displayReviews} reviews)</span>
                  </div>
                  <Badge className="mb-4">{displayExperience}</Badge>
                  <div className="space-y-2 text-sm text-gray-600">
                    <div className="flex items-center justify-center space-x-2">
                      <MapPinIcon className="w-4 h-4" />
                      <span>{locationText}</span>
                    </div>
                    <div className="flex items-center justify-center space-x-2">
                      <ClockIcon className="w-4 h-4" />
                      <span>₹{displayConsultationFee} consultation</span>
                    </div>
                  </div>
                </div>

                {/* Doctor Details */}
                <div className="lg:col-span-2 p-4 sm:p-6 lg:p-8">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Contact</h3>
                      <p className="text-gray-600">{doctor.contactNumber || "Not available"}</p>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Languages</h3>
                      <p className="text-gray-600">{languagesList.length > 0 ? languagesList.join(", ") : "Not specified"}</p>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Experience</h3>
                      <p className="text-gray-600">{displayExperience} {typeof displayExperience === 'string' && !displayExperience.includes('years') && !displayExperience.includes('+') ? 'years' : ''}</p>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Available Cities</h3>
                      <p className="text-gray-600">
                        {doctor.cities && doctor.cities.length > 0 
                          ? doctor.cities.join(", ") 
                          : "Not specified"}
                      </p>
                    </div>
                  </div>

                  {doctor.about && (
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">
                        About {doctor.professionalTitle || 'Dr.'} {(displayName || '').split(' ').pop() || ''}
                      </h3>
                      <p className="text-gray-600 leading-relaxed whitespace-pre-line">{doctor.about}</p>
                    </div>
                  )}

                  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                    <Button size="lg" className="bg-gray-900 hover:bg-gray-600 text-sm sm:text-base" onClick={() => openWhatsApp(contactNumber, "Hi, I am contacting you for treatment from TheLivCure site.")}>
                      <PhoneIcon className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                      Call Now
                    </Button>
                    <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-sm sm:text-base" onClick={() => setIsBookingOpen(true)}>
                      <CalendarIcon className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                      Book Appointment
                    </Button>
                    <Button size="lg" variant="outline" className="text-sm sm:text-base">
                      <HeartIcon className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                      Save Doctor
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Detailed Information */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6 sm:space-y-8">
              {/* Education/Qualifications */}
              {(educationList.length > 0 || (doctor.qualifications && doctor.qualifications.length > 0)) && (
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center space-x-2 mb-4">
                      <GraduationCapIcon className="w-6 h-6 text-blue-600" />
                      <h2 className="text-xl font-bold text-gray-900">Education & Qualifications</h2>
                    </div>
                    <div className="space-y-3">
                      {educationList.length > 0 ? (
                        educationList.map((edu, index) => (
                          <div key={index} className="flex items-start space-x-3">
                            <CheckCircleIcon className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-600">{edu}</span>
                          </div>
                        ))
                      ) : (
                        doctor.qualifications && doctor.qualifications.map((qual, index) => (
                          <div key={index} className="flex items-start space-x-3">
                            <CheckCircleIcon className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                            <div className="text-gray-600">
                              <span className="font-semibold">{qual.degree || ''}</span>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Specializations */}
              {specializationsList.length > 0 && (
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center space-x-2 mb-4">
                      <BookOpenIcon className="w-6 h-6 text-blue-600" />
                      <h2 className="text-xl font-bold text-gray-900">Specializations</h2>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {specializationsList.map((spec, index) => (
                        <Badge key={index} variant="secondary">{spec}</Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Achievements - Only show if there are achievements */}
              {achievementsList.length > 0 && (
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center space-x-2 mb-4">
                      <AwardIcon className="w-6 h-6 text-blue-600" />
                      <h2 className="text-xl font-bold text-gray-900">Achievements & Awards</h2>
                    </div>
                    <div className="space-y-3">
                      {achievementsList.map((achievement, index) => (
                        <div key={index} className="flex items-start space-x-3">
                          <CheckCircleIcon className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-600">{achievement}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Patient Reviews */}
              <Card>
                <CardContent className="p-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-6">Patient Reviews</h2>
                  <div className="space-y-6">
                    {patientReviews.map((review, index) => (
                      <div key={index} className="border-b border-gray-100 pb-4 last:border-b-0">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                              <UserIcon className="w-5 h-5 text-white" />
                            </div>
                            <div>
                              <div className="font-semibold text-gray-900">{review.name}</div>
                              <div className="text-sm text-gray-500">{review.date}</div>
                            </div>
                          </div>
                          <div className="flex">
                            {[...Array(review.rating)].map((_, i) => (
                              <StarIcon key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                            ))}
                          </div>
                        </div>
                        <p className="text-gray-600">{review.review}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Appointment Booking */}
              <Card className="sticky top-4">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Book Appointment</h3>
                  
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Available Time Slots
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {availableSlots.map((slot, index) => (
                        <button
                          key={index}
                          onClick={() => setSelectedTimeSlot(slot)}
                          className={`p-2 text-sm rounded-lg border transition-colors ${
                            selectedTimeSlot === slot
                              ? 'bg-blue-600 text-white border-blue-600'
                              : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100'
                          }`}
                        >
                          {slot}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-3 mb-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Consultation Fee:</span>
                      <span className="font-semibold">₹{displayConsultationFee}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Status:</span>
                      <span className={`font-semibold ${doctor.status === 'active' ? 'text-green-600' : 'text-gray-600'}`}>
                        {doctor.status === 'active' ? 'Available' : 'Not Available'}
                      </span>
                    </div>
                    {doctor.isVisitingDoctor && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Type:</span>
                        <span className="font-semibold text-blue-600">Visiting Doctor</span>
                      </div>
                    )}
                    {doctor.isHospitalDoctor && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Type:</span>
                        <span className="font-semibold text-purple-600">Hospital Doctor</span>
                      </div>
                    )}
                  </div>

                  <Button className="w-full bg-blue-600 hover:bg-blue-700 mb-3" onClick={() => setIsBookingOpen(true)}>
                    Book Consultation
                  </Button>
                  
                  <Button variant="outline" className="w-full" onClick={() => openWhatsApp(contactNumber, "Hi, I am contacting you for treatment from TheLivCure site.")}>
                    <PhoneIcon className="w-4 h-4 mr-2" />
                    Call Doctor
                  </Button>
                </CardContent>
              </Card>

              {/* Quick Stats */}
              <Card>
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Quick Stats</h3>
                  <div className="space-y-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-purple-600">{displayRating}/5</div>
                      <div className="text-sm text-gray-600">Patient Rating</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-600">{displayReviews}</div>
                      <div className="text-sm text-gray-600">Total Reviews</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600">₹{displayConsultationFee}</div>
                      <div className="text-sm text-gray-600">Consultation Fee</div>
                    </div>
                    {doctor.cities && doctor.cities.length > 0 && (
                      <div className="text-center">
                        <div className="text-2xl font-bold text-orange-600">{doctor.cities.length}</div>
                        <div className="text-sm text-gray-600">Available Cities</div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      {/* Controlled Booking Modal */}
      <BookAppointmentModal
        isOpen={isBookingOpen}
        onOpenChange={setIsBookingOpen}
        treatmentType="general-consultation"
        doctorName={displayName}
      />
    </div>
  );
};

export default DoctorProfile;